import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import JobCard from '../../components/careers/JobCard';
import JdModal from '../../components/careers/JdModal';
import { jobService } from '../../services/jobService';
import { applicationService } from '../../services/applicationService';
import { FALLBACK_JOBS } from '../../services/fallbackData';
import { useSite } from '../../context/SiteContext';

export default function CareersPage() {
  const { settings } = useSite();
  const [jobs, setJobs] = useState(FALLBACK_JOBS);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isJdOpen, setIsJdOpen] = useState(false);

  // Inline Form State
  const [inlineData, setInlineData] = useState({
    full_name: '',
    email: '',
    phone: '',
    position_key: '',
    note: ''
  });
  const [inlineFile, setInlineFile] = useState(null);
  const [inlineLoading, setInlineLoading] = useState(false);
  const [inlineMsg, setInlineMsg] = useState({ text: '', type: '' });
  const [inlineSuccess, setInlineSuccess] = useState(null);

  useEffect(() => {
    jobService
      .getPublicJobs()
      .then((res) => {
        if (res.data && res.data.length > 0) {
          setJobs(res.data);
        }
      })
      .catch(() => { });
  }, []);

  const handleOpenDetail = (job) => {
    setSelectedJob(job);
    setIsJdOpen(true);
  };

  const handleApplyFromModal = (job) => {
    setInlineData((prev) => ({ ...prev, position_key: job.key }));
    // Đợi JD modal đóng xong (bỏ khoá scroll body) rồi mới cuộn xuống form.
    setTimeout(() => {
      document.getElementById('careerApplyWrap')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleInlineSubmit = async (e) => {
    e.preventDefault();
    const { full_name, email, phone, position_key, note } = inlineData;

    if (!full_name || !email || !phone || !inlineFile) {
      setInlineMsg({ text: 'Vui lòng điền đầy đủ các trường và đính kèm CV.', type: 'error' });
      return;
    }

    const selected = jobs.find((j) => j.key === position_key);
    const position_label = selected ? selected.title : position_key === '__other__' ? 'Vị trí khác' : position_key;

    const data = new FormData();
    data.append('full_name', full_name);
    data.append('email', email);
    data.append('phone', phone);
    data.append('position_key', position_key === '__other__' ? '' : position_key);
    data.append('position_label', position_label);
    data.append('note', note);
    data.append('source', 'tuyen-dung-inline');
    data.append('cv', inlineFile);

    setInlineLoading(true);
    setInlineMsg({ text: '', type: '' });

    try {
      const res = await applicationService.submitApplication(data);
      setInlineSuccess({
        ...res.data,
        email,
        phone,
        position_label
      });
    } catch (err) {
      setInlineMsg({ text: err.message || 'Lỗi gửi hồ sơ. Vui lòng thử lại.', type: 'error' });
    } finally {
      setInlineLoading(false);
    }
  };

  return (
    <div>
      {/* Page Hero */}
      <section className="page-hero page-hero--split">
        <div className="container">
          <div className="crumb">
            <Link to="/">Trang chủ</Link> / <span>Tuyển dụng</span>
          </div>
          <div className="page-hero__grid">
            <div className="page-hero__text">
              <span className="eyebrow">Cơ hội nghề nghiệp</span>
              <h1>
                Cùng Thế Thảo viết tiếp
                <br />
                hành trình ngành Dược.
              </h1>
              <p className="lead">
                Hơn 12 năm đồng hành cùng 20.000+ nhà thuốc Việt Nam — chúng tôi đang tìm những con người tử tế, ham học và dám làm để cùng vươn xa.
              </p>
              <div className="page-hero__cta">
                <a className="btn btn--primary" href="#vi-tri-dang-tuyen">
                  Xem vị trí đang tuyển
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 5v14M5 12l7 7 7-7" />
                  </svg>
                </a>
              </div>
            </div>
            <div className="page-hero__media">
              <figure className="hero-photo">
                <img
                  src="https://picsum.photos/seed/thethao-careers/1000/1000"
                  alt="Đội ngũ Thế Thảo tại văn phòng"
                  loading="eager"
                  width="1200"
                  height="900"
                />
                <figcaption className="hero-photo__caption">Đội ngũ Thế Thảo · 2026</figcaption>
              </figure>
            </div>
          </div>
        </div>
      </section>

      {/* 4 Lý do chọn Thế Thảo */}
      <section className="section">
        <div className="container">
          <div className="text-center" style={{ maxWidth: '720px', marginInline: 'auto' }}>
            <span className="eyebrow">Vì sao chọn Thế Thảo</span>
            <h2>Bốn lý do để gắn bó lâu dài</h2>
            <p className="lead" style={{ marginInline: 'auto' }}>
              Không hứa hẹn viển vông — chỉ là những điều chúng tôi đã thực sự xây dựng trong suốt 12 năm.
            </p>
          </div>

          <div className="mission-grid mission-grid--4" style={{ marginTop: '36px' }}>
            <div className="mission-card">
              <div className="icon-wrap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="8" r="7" />
                  <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
                </svg>
              </div>
              <h3>12 năm uy tín</h3>
              <p>Thương hiệu vững chắc, chuẩn GDP, đối tác của 20.000+ nhà thuốc toàn quốc.</p>
            </div>

            <div className="mission-card">
              <div className="icon-wrap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
              </div>
              <h3>Thu nhập xứng đáng</h3>
              <p>Lương cứng cạnh tranh, hoa hồng không giới hạn, thưởng KPI rõ ràng và minh bạch.</p>
            </div>

            <div className="mission-card">
              <div className="icon-wrap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                </svg>
              </div>
              <h3>Đào tạo thực chiến</h3>
              <p>Mentor kèm 1-1 trong 3 tháng đầu, tài trợ các khoá học quản lý từ PTI, HBR.</p>
            </div>

            <div className="mission-card">
              <div className="icon-wrap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <h3>Văn hoá trẻ trung</h3>
              <p>Môi trường Gen Z thân thiện, du lịch thường niên, liên hoan sinh nhật quý.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Đãi ngộ & Phúc lợi */}
      <section className="section section--soft">
        <div className="container">
          <div className="text-center" style={{ maxWidth: '720px', marginInline: 'auto' }}>
            <span className="eyebrow">Chính sách đãi ngộ</span>
            <h2>Chế độ &amp; Quyền lợi nhân sự</h2>
          </div>

          <div className="pod-grid" style={{ marginTop: '36px' }}>
            <article className="pod-card">
              <div className="pod-header">
                <div className="pod-num">01</div>
                <span className="pod-kw">Thu nhập</span>
              </div>
              <h3>Lương cạnh tranh — thưởng minh bạch</h3>
              <ul className="pod-list">
                <li>Lương cứng theo năng lực, review định kỳ 6 tháng/lần</li>
                <li>Thưởng tháng 13, thưởng KPI theo quý</li>
                <li>Hoa hồng hấp dẫn theo doanh số cho khối kinh doanh</li>
                <li>Phụ cấp đi lại, gửi xe, ăn trưa</li>
              </ul>
            </article>

            <article className="pod-card">
              <div className="pod-header">
                <div className="pod-num">02</div>
                <span className="pod-kw">Phát triển</span>
              </div>
              <h3>Học không dừng — Tiến không nghỉ</h3>
              <ul className="pod-list">
                <li>Onboarding 2 tuần + mentor 1-1 trong 3 tháng đầu</li>
                <li>Workshop chuyên môn hàng tháng miễn phí</li>
                <li>Hỗ trợ chứng chỉ chuyên môn và nâng cao kỹ năng</li>
                <li>Lộ trình thăng tiến rõ ràng theo khung năng lực</li>
              </ul>
            </article>

            <article className="pod-card">
              <div className="pod-header">
                <div className="pod-num">03</div>
                <span className="pod-kw">Phúc lợi</span>
              </div>
              <h3>An tâm sống — vững tâm làm</h3>
              <ul className="pod-list">
                <li>BHXH, BHYT, BHTN đóng đủ trên lương thực nhận</li>
                <li>Khám sức khoẻ định kỳ 1 lần/năm</li>
                <li>Nghỉ phép 12 ngày/năm + quà tặng sinh nhật</li>
                <li>Team building, dã ngoại, du lịch hàng năm</li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* Quy trình ứng tuyển */}
      <section className="section">
        <div className="container">
          <div className="text-center" style={{ maxWidth: '680px', marginInline: 'auto' }}>
            <span className="eyebrow">Quy trình ứng tuyển</span>
            <h2>Bốn bước — gọn gàng, tôn trọng thời gian của bạn</h2>
          </div>

          <div className="values-grid values-grid--4" style={{ marginTop: '36px' }}>
            <div className="value-card">
              <div className="value-num">01</div>
              <h3>Gửi CV</h3>
              <p>
                Qua email <a href={`mailto:${settings.contact_email}`}>{settings.contact_email}</a> hoặc form bên dưới. Phản hồi trong 3 ngày làm việc.
              </p>
            </div>
            <div className="value-card">
              <div className="value-num">02</div>
              <h3>Phỏng vấn vòng 1</h3>
              <p>Trao đổi 30–45 phút với HR &amp; quản lý trực tiếp. Online hoặc tại văn phòng Hà Nội.</p>
            </div>
            <div className="value-card">
              <div className="value-num">03</div>
              <h3>Bài test thực tế</h3>
              <p>Bài tập nhỏ liên quan công việc thật — không quá 2 giờ. Có phản hồi cụ thể dù bạn pass hay không.</p>
            </div>
            <div className="value-card">
              <div className="value-num">04</div>
              <h3>Offer &amp; Onboard</h3>
              <p>Thư mời nhận việc trong 5 ngày làm việc sau vòng cuối. Onboarding 2 tuần có mentor đồng hành.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Vị trí đang tuyển */}
      <section className="section" id="vi-tri-dang-tuyen">
        <div className="container">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <div>
              <span className="eyebrow">Vị trí đang tuyển</span>
              <h2>Tham gia đội ngũ — {jobs.length} vị trí đang mở</h2>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              <a className="btn btn--ghost" href="#careerApplyWrap">
                Gửi CV qua email
                <svg className="arr" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>

          <div className="areas-grid jobs-grid" style={{ gap: '24px', marginTop: '32px' }}>
            {jobs.map((job) => (
              <JobCard key={job.id} job={job} onOpenDetail={handleOpenDetail} />
            ))}
          </div>
        </div>
      </section>

      {/* Liên hệ & Ứng tuyển */}
      <section className="section section--soft" id="careerApplyWrap">
        <div className="container contact-grid">
          <div className="contact-info reveal">
            <span className="eyebrow">Liên hệ phòng Nhân sự</span>
            <h2>Sẵn sàng nói chuyện với bạn.</h2>
            <p style={{ maxWidth: '50ch' }}>
              Một cuộc gọi hoặc một email — đôi khi đó là tất cả những gì cần để bắt đầu hành trình mới. Đội ngũ HR của Thế Thảo sẽ phản hồi trong 24 giờ làm việc.
            </p>

            <div className="info-row">
              <div className="icon-wrap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </div>
              <div>
                <strong>Văn phòng Hà Nội</strong>
                {settings.contact_address}
              </div>
            </div>

            <div className="info-row">
              <div className="icon-wrap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16v16H4z" />
                  <path d="M4 4l8 8 8-8" />
                </svg>
              </div>
              <div>
                <strong>Email tuyển dụng</strong>
                <a href={`mailto:${settings.jobs_default_email}?subject=Ứng tuyển - Thế Thảo Pharma`}>{settings.jobs_default_email}</a>
              </div>
            </div>

            <div className="info-row">
              <div className="icon-wrap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92V21a1 1 0 0 1-1.1 1 19 19 0 0 1-8.3-3 19 19 0 0 1-6-6 19 19 0 0 1-3-8.4A1 1 0 0 1 4.6 3.6h4a1 1 0 0 1 1 .8c.13 1 .35 2 .67 2.9a1 1 0 0 1-.23 1L8.1 9.9a16 16 0 0 0 6 6l1.6-1.95a1 1 0 0 1 1-.23c.9.3 1.9.55 2.9.66a1 1 0 0 1 .8 1z" />
                </svg>
              </div>
              <div>
                <strong>Hotline HR</strong>
                {settings.contact_phone_hr}
              </div>
            </div>

            <div className="info-row">
              <div className="icon-wrap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" />
                </svg>
              </div>
              <div>
                <strong>Giờ làm việc</strong>
                Thứ Hai — Thứ Bảy: 8:00 – 17:30
              </div>
            </div>
          </div>

          <div className="contact-form reveal">
            {!inlineSuccess ? (
              <form onSubmit={handleInlineSubmit} className="career-form">
                <h3>Gửi hồ sơ ứng tuyển</h3>
                <div className="field">
                  <label htmlFor="careerName">Họ và tên <em>*</em></label>
                  <input
                    id="careerName"
                    type="text"
                    value={inlineData.full_name}
                    onChange={(e) => setInlineData({ ...inlineData, full_name: e.target.value })}
                    placeholder="Ví dụ: Nguyễn Văn A"
                    required
                  />
                </div>

                <div className="field-row">
                  <div className="field">
                    <label htmlFor="careerEmail">Email <em>*</em></label>
                    <input
                      id="careerEmail"
                      type="email"
                      value={inlineData.email}
                      onChange={(e) => setInlineData({ ...inlineData, email: e.target.value })}
                      placeholder="example@gmail.com"
                      required
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="careerPhone">Số điện thoại <em>*</em></label>
                    <input
                      id="careerPhone"
                      type="tel"
                      value={inlineData.phone}
                      onChange={(e) => setInlineData({ ...inlineData, phone: e.target.value })}
                      placeholder="0989 xxx xxx"
                      required
                    />
                  </div>
                </div>

                <div className="field">
                  <label htmlFor="careerPosition">Vị trí ứng tuyển <em>*</em></label>
                  <select
                    id="careerPosition"
                    value={inlineData.position_key}
                    onChange={(e) => setInlineData({ ...inlineData, position_key: e.target.value })}
                    required
                  >
                    <option value="">— Chọn vị trí —</option>
                    {jobs.map((j) => (
                      <option key={j.key} value={j.key}>
                        {j.title} {j.headcount > 0 ? `· Tuyển ${j.headcount}` : ''}
                      </option>
                    ))}
                    <option value="__other__">Vị trí khác</option>
                  </select>
                </div>

                <div className="field">
                  <label htmlFor="careerCv">CV của bạn <em>*</em></label>
                  <input
                    id="careerCv"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => setInlineFile(e.target.files[0] || null)}
                    required
                  />
                  <span className="field-hint">PDF / DOC / DOCX, tối đa 10 MB.</span>
                </div>

                <div className="field">
                  <label htmlFor="careerNote">Giới thiệu ngắn về bạn</label>
                  <textarea
                    id="careerNote"
                    rows="3"
                    value={inlineData.note}
                    onChange={(e) => setInlineData({ ...inlineData, note: e.target.value })}
                    placeholder="Kinh nghiệm, lý do quan tâm Thế Thảo, thời gian rảnh phỏng vấn…"
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn--primary career-form__submit"
                  disabled={inlineLoading}
                  style={{ width: '100%', justifyContent: 'center' }}
                >
                  <span className="career-form__submit-text">{inlineLoading ? 'Đang gửi hồ sơ…' : 'Gửi ứng tuyển'}</span>
                  <svg className="arr" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M13 5l7 7-7 7" />
                  </svg>
                </button>

                {inlineMsg.text && (
                  <p className={`career-form__status ${inlineMsg.type === 'error' ? 'is-error' : 'is-success'}`}>
                    {inlineMsg.text}
                  </p>
                )}
                <p className="form-note" style={{ marginTop: '14px', fontSize: '.88rem', color: 'var(--c-ink-500)' }}>
                  Thông tin của bạn được bảo mật và chỉ sử dụng cho mục đích tuyển dụng.
                </p>
              </form>
            ) : (
              <div className="career-success">
                <div className="apply-success__icon" aria-hidden="true">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <h3>Đã nhận hồ sơ của bạn</h3>
                <p className="apply-modal__lead">
                  Cảm ơn <strong>{inlineSuccess.full_name}</strong>! Bộ phận HR của Thế Thảo Pharma sẽ liên hệ qua email <strong>{inlineSuccess.email}</strong> hoặc số điện thoại <strong>{inlineSuccess.phone}</strong> trong vòng <strong>24 giờ làm việc</strong>.
                </p>
                <dl className="apply-success__meta">
                  <div><dt>Vị trí ứng tuyển</dt><dd>{inlineSuccess.position_label}</dd></div>
                  <div><dt>Mã hồ sơ</dt><dd className="apply-success__ref">{inlineSuccess.ref}</dd></div>
                </dl>
                <p className="apply-success__hint">Bạn có thể chụp lại mã hồ sơ phòng khi cần đối chiếu với HR.</p>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => setInlineSuccess(null)}
                >
                  Nộp thêm hồ sơ khác
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      <JdModal
        job={selectedJob}
        isOpen={isJdOpen}
        onClose={() => setIsJdOpen(false)}
        onApply={handleApplyFromModal}
      />
    </div>
  );
}
